import { Search, TaskStatus } from '../../../types/search.types';

export type SearchPipelineVariant = 'regular' | 'smart-filter' | 'no-website';
export type SearchPipelineStepId =
  | 'pending'
  | 'businesses'
  | 'websites'
  | 'profiles'
  | 'decision_makers'
  | 'contacts'
  | 'preparing'
  | 'completed';
export type SearchPipelineStepState = 'done' | 'active' | 'upcoming';

export interface PipelineStepDefinition {
  id: SearchPipelineStepId;
  label: string;
  compactLabel: string;
  description: string;
}

export interface SearchPipelineStep extends PipelineStepDefinition {
  state: SearchPipelineStepState;
}

export interface SearchPipeline {
  variant: SearchPipelineVariant;
  steps: SearchPipelineStep[];
  activeStep: SearchPipelineStep | null;
  failed: boolean;
  supportingText: string;
}

const STEP_DEFINITIONS: Record<SearchPipelineStepId, PipelineStepDefinition> = {
  pending: {
    id: 'pending',
    label: 'Waiting to start',
    compactLabel: 'Waiting',
    description: 'Your search is waiting to begin.',
  },
  businesses: {
    id: 'businesses',
    label: 'Finding businesses',
    compactLabel: 'Businesses',
    description: 'Searching the selected area for matching businesses.',
  },
  websites: {
    id: 'websites',
    label: 'Checking business websites',
    compactLabel: 'Websites',
    description: 'Checking company websites for useful business and contact details.',
  },
  profiles: {
    id: 'profiles',
    label: 'Checking public business profiles',
    compactLabel: 'Public profiles',
    description: 'Checking trusted public business profiles for additional details.',
  },
  decision_makers: {
    id: 'decision_makers',
    label: 'Finding decision makers',
    compactLabel: 'Decision makers',
    description: 'Looking for owners, managers, and other relevant people.',
  },
  contacts: {
    id: 'contacts',
    label: 'Finding contact details',
    compactLabel: 'Contact details',
    description: 'Matching useful contact details to the leads we found.',
  },
  preparing: {
    id: 'preparing',
    label: 'Preparing results',
    compactLabel: 'Preparing',
    description: 'Cleaning and organizing your lead list.',
  },
  completed: {
    id: 'completed',
    label: 'Results ready',
    compactLabel: 'Ready',
    description: 'Your results are ready.',
  },
};

const REGULAR_STEP_IDS: SearchPipelineStepId[] = [
  'pending',
  'businesses',
  'websites',
  'decision_makers',
  'contacts',
  'preparing',
  'completed',
];

const NO_WEBSITE_STEP_IDS: SearchPipelineStepId[] = [
  'pending',
  'businesses',
  'profiles',
  'preparing',
  'completed',
];

const isPipelineStepId = (value: string | null): value is SearchPipelineStepId => (
  value !== null && Object.prototype.hasOwnProperty.call(STEP_DEFINITIONS, value)
);

const formatCount = (value: number | undefined): string => (value || 0).toLocaleString();

const isFinished = (completed: number | undefined, total: number | undefined): boolean => (
  (total || 0) === 0 || (completed || 0) >= (total || 0)
);

export const getSearchPipelineVariant = (search: Search): SearchPipelineVariant => {
  if (search.progress_pipeline) return search.progress_pipeline.variant;
  if (search.google_maps_requirements?.has_website === false) return 'no-website';
  return search.website_filter ? 'smart-filter' : 'regular';
};

const getVariantStepIds = (variant: SearchPipelineVariant): SearchPipelineStepId[] => (
  variant === 'no-website' ? NO_WEBSITE_STEP_IDS : REGULAR_STEP_IDS
);

const getApplicableStepIds = (
  search: Search,
  variant: SearchPipelineVariant,
): SearchPipelineStepId[] => {
  const serverStepIds = search.progress_pipeline?.step_ids
    .filter((stepId): stepId is SearchPipelineStepId => isPipelineStepId(stepId)) || [];
  return serverStepIds.length > 0 ? serverStepIds : getVariantStepIds(variant);
};

export const getApplicablePipelineSteps = (
  variant: SearchPipelineVariant,
): PipelineStepDefinition[] => getVariantStepIds(variant).map((stepId) => STEP_DEFINITIONS[stepId]);

const getProcessingFallbackStep = (
  search: Search,
  variant: SearchPipelineVariant,
): SearchPipelineStepId => {
  const mapsFinished = (
    search.total_points > 0
    && isFinished(search.processed_points, search.total_points)
    && isFinished(
      (search.completed_links || 0) + (search.failed_links || 0),
      search.total_links,
    )
  );
  if (!mapsFinished) return 'businesses';

  if (variant === 'no-website') {
    return search.website_scraping_status === 'COMPLETED'
      || search.website_scraping_status === 'NOT_APPLICABLE'
      ? 'preparing'
      : 'profiles';
  }

  if (
    search.page_checker_status === 'PENDING'
    || search.page_checker_status === 'IN_PROGRESS'
    || !isFinished(search.checked_pages_count, search.total_pages_to_check)
  ) return 'websites';

  if (
    search.website_scraping_status === 'PENDING'
    || search.website_scraping_status === 'IN_PROGRESS'
    || !isFinished(
      search.website_scraped_places_count,
      search.total_places_for_website_scraping,
    )
  ) return 'decision_makers';

  if (
    (search.total_pages_to_check || 0) === 0
    && (search.total_places_for_website_scraping || 0) === 0
  ) return 'websites';
  return 'decision_makers';
};

const getFallbackActiveStepId = (
  search: Search,
  variant: SearchPipelineVariant,
  stepIds: SearchPipelineStepId[],
): SearchPipelineStepId | null => {
  const status = String(search.status);
  if (status === TaskStatus.COMPLETED) return 'completed';
  if (status === TaskStatus.FAILED) return null;
  if (['IN_QUEUE', TaskStatus.QUEUED, TaskStatus.PENDING].includes(status)) return 'pending';
  if (status === TaskStatus.NORMALIZATION) return 'preparing';
  if (variant === 'no-website' && [
    TaskStatus.DATA_COLLECTED,
    TaskStatus.VALIDATION,
    TaskStatus.EMAIL_DISCOVERY,
  ].includes(status as TaskStatus)) return 'profiles';
  if (status === TaskStatus.EMAIL_DISCOVERY) {
    return stepIds.includes('contacts') ? 'contacts' : 'preparing';
  }
  if ([TaskStatus.DATA_COLLECTED, TaskStatus.VALIDATION].includes(status as TaskStatus)) {
    return stepIds.includes('decision_makers') ? 'decision_makers' : 'preparing';
  }
  return getProcessingFallbackStep(search, variant);
};

const getSupportingText = (
  search: Search,
  activeStepId: SearchPipelineStepId | null,
  failed: boolean,
): string => {
  if (failed) return search.error || 'The search stopped before it could finish.';
  switch (activeStepId) {
    case 'businesses': {
      const outcomes: string[] = [];
      if (search.total_points > 0) {
        outcomes.push(
          `${formatCount(Math.min(search.processed_points, search.total_points))}/${formatCount(search.total_points)} areas searched`,
        );
      }
      outcomes.push(`${formatCount(search.completed_links)} businesses found`);
      return outcomes.join(' · ');
    }
    case 'websites':
      return (search.total_pages_to_check || 0) > 0
        ? `${formatCount(search.checked_pages_count)}/${formatCount(search.total_pages_to_check)} websites checked`
        : 'Preparing company websites for review.';
    case 'profiles':
      return (search.total_places_for_website_scraping || 0) > 0
        ? `${formatCount(search.website_scraped_places_count)}/${formatCount(search.total_places_for_website_scraping)} profile checks complete`
        : STEP_DEFINITIONS.profiles.description;
    case 'decision_makers':
      return (search.total_places_for_website_scraping || 0) > 0
        ? `${formatCount(search.website_scraped_places_count)}/${formatCount(search.total_places_for_website_scraping)} websites reviewed`
        : STEP_DEFINITIONS.decision_makers.description;
    case 'contacts':
      return STEP_DEFINITIONS.contacts.description;
    case 'preparing':
      return (search.total_places_for_normalization || 0) > 0
        ? `${formatCount(search.normalized_places_count)}/${formatCount(search.total_places_for_normalization)} businesses prepared`
        : STEP_DEFINITIONS.preparing.description;
    case 'pending':
    case 'completed':
      return STEP_DEFINITIONS[activeStepId].description;
    default:
      return '';
  }
};

export const getSearchPipeline = (search: Search): SearchPipeline => {
  const variant = getSearchPipelineVariant(search);
  const stepIds = getApplicableStepIds(search, variant);
  const failed = [TaskStatus.FAILED, 'STOPPED'].includes(String(search.status));
  const serverActiveStepId = search.progress_pipeline?.active_step_id ?? null;
  const activeStepId = search.progress_pipeline
    ? (isPipelineStepId(serverActiveStepId) ? serverActiveStepId : null)
    : getFallbackActiveStepId(search, variant, stepIds);
  const activeIndex = activeStepId ? stepIds.indexOf(activeStepId) : -1;
  const definitions = stepIds.map((stepId) => {
    const definition = STEP_DEFINITIONS[stepId];
    if (stepId === 'websites' && variant === 'smart-filter') {
      return {
        ...definition,
        description: 'Reviewing company websites for relevance and useful contact details.',
      };
    }
    return definition;
  });
  const steps = definitions.map<SearchPipelineStep>((step, index) => ({
    ...step,
    state: failed
      ? 'upcoming'
      : activeStepId === 'completed' || index < activeIndex
        ? 'done'
        : index === activeIndex
          ? 'active'
          : 'upcoming',
  }));
  const activeStep = activeStepId
    ? steps.find((step) => step.id === activeStepId) || null
    : null;

  return {
    variant,
    steps,
    activeStep,
    failed,
    supportingText: getSupportingText(search, activeStepId, failed),
  };
};

export const getSearchProgressPercent = (search: Search): number => {
  if (String(search.status) === TaskStatus.COMPLETED) return 100;
  if (['IN_QUEUE', TaskStatus.QUEUED, TaskStatus.PENDING].includes(String(search.status))) {
    return 0;
  }
  return Math.min(99, Math.max(0, Math.round(search.progress || 0)));
};

export const getPipelineStepIndex = (
  status: string,
  steps: PipelineStepDefinition[] = SEARCH_PIPELINE_STEPS,
): number => {
  const variant: SearchPipelineVariant = steps.some((step) => step.id === 'profiles')
    ? 'no-website'
    : 'regular';
  const search = {
    status,
    total_points: 0,
    processed_points: 0,
    total_links: 0,
    completed_links: 0,
  } as Search;
  const activeStepId = getFallbackActiveStepId(
    search,
    variant,
    steps.map((step) => step.id),
  );
  return steps.findIndex((step) => step.id === activeStepId);
};

export const SEARCH_PIPELINE_STEPS = getApplicablePipelineSteps('regular');
export const NO_WEBSITE_PIPELINE_STEPS = getApplicablePipelineSteps('no-website');
